@tailwind base;
@tailwind components;
@tailwind utilities;

.contextual-note-highlight,
span[data-contextual-note-highlight="true"] {
  color: #2563eb;
  font-weight: 600;
}

@layer base {
  html {
    height: 100%;
    width: 100%;
    margin: 0;
    padding: 0;
  }

  /* min-height (not height): a sticky element can never leave its parent's box, so if #root
     is hard-capped at one viewport height the sticky navbar stops sticking as soon as the
     user scrolls past the first screenful. min-height lets body/#root grow with the page so
     the navbar's containing block spans the whole document. */
  body,
  #root {
    min-height: 100%;
    width: 100%;
    margin: 0;
    padding: 0;
  }

  /* The app has no legitimate reason to scroll horizontally at the page level - individual
     widgets (e.g. type tabs) manage their own horizontal scrolling internally. Guard against
     third-party libraries (e.g. vaul's drawer position-fixing) leaving the body wider than the
     viewport and exposing a page-level horizontal scrollbar.
     Uses `clip` (not `hidden`): overflow-x hidden turns body into a scroll container, which
     silently breaks every `position: sticky` descendant (e.g. the navbar stopped sticking).
     `clip` clips the same overflow without creating a scroll container. The `hidden` line is
     only a fallback for old browsers that don't support `clip`. */
  html,
  body {
    overflow-x: hidden;
    overflow-x: clip;
  }

  :root {
    --background: 0 0% 100%;
    --foreground: 222.2 47.4% 11.2%;

    --muted: 210 40% 96.1%;
    --muted-foreground: 215.4 16.3% 46.9%;

    --popover: 0 0% 100%;
    --popover-foreground: 222.2 47.4% 11.2%;

    --card: 0 0% 100%;
    --card-foreground: 222.2 47.4% 11.2%;

    --border: 214.3 31.8% 91.4%;
    --input: 214.3 31.8% 91.4%;

    --primary: 222.2 47.4% 11.2%;
    --primary-foreground: 210 40% 98%;

    --secondary: 210 40% 96.1%;
    --secondary-foreground: 222.2 47.4% 11.2%;

    --accent: 210 40% 96.1%;
    --accent-foreground: 222.2 47.4% 11.2%;

    --destructive: 0 86% 59%;
    --destructive-foreground: 210 40% 98%;

    --ring: 215 20.2% 65.1%;

    --radius: 0.5rem;

    --highlight: oklch(0.852 0.199 91.936);

    --brand: oklch(0.623 0.214 259.815);
  }

  .dark {
    --background: 224 71% 4%;
    --foreground: 213 31% 91%;

    --muted: 223 47% 11%;
    --muted-foreground: 215.4 16.3% 56.9%;

    --popover: 224 71% 4%;
    --popover-foreground: 215 20.2% 65.1%;

    --card: 224 71% 4%;
    --card-foreground: 213 31% 91%;

    --border: 216 34% 17%;
    --input: 216 34% 17%;

    --primary: 210 40% 98%;
    --primary-foreground: 222.2 47.4% 1.2%;

    --secondary: 222.2 47.4% 11.2%;
    --secondary-foreground: 210 40% 98%;

    --accent: 216 34% 17%;
    --accent-foreground: 210 40% 98%;

    --destructive: 0 63% 31%;
    --destructive-foreground: 210 40% 98%;

    --ring: 216 34% 17%;

    --radius: 0.5rem;

    --highlight: oklch(0.852 0.199 91.936);

    --brand: oklch(0.707 0.165 254.624);
  }

  body {
    @apply bg-background text-foreground;
    font-family: sans-serif;
  }
}

@layer base {
  * {
    @apply border-border;
  }

  h1 {
    @apply text-3xl font-bold;
  }

  h2 {
    @apply text-2xl font-bold;
  }

  h3 {
    @apply text-xl font-semibold;
  }

  h4 {
    @apply text-lg font-semibold;
  }

  h5 {
    @apply text-base font-medium;
  }

  h6 {
    @apply text-sm font-medium;
  }

  /* Remove underlines from headings */
  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    text-decoration: none !important;
    border-bottom: none !important;
  }
}

.h-70vh {
  height: 70vh;
}

.HW_badge_cont.HW_visible {
  position: relative;
  top: -35px;
}

/* Streaming content styles for markdown display */
.streaming-content {
  /* Match Plate [data-slate-editor]. `prose-sm` defaults to ~1.71. */
  line-height: 1.45;
}

.streaming-content ul {
  list-style-type: disc !important;
  margin-left: 1.5rem !important;
  margin-top: 0.5rem !important;
  margin-bottom: 0.5rem !important;
}

.streaming-content li {
  margin-bottom: 0.15rem !important;
  display: list-item !important;
  line-height: 1.45 !important;
}

.streaming-content h1,
.streaming-content h2,
.streaming-content h3,
.streaming-content h4,
.streaming-content h5,
.streaming-content h6 {
  margin-top: 0.75rem !important;
  margin-bottom: 0.25rem !important;
}

.streaming-content h1 {
  margin-top: 0 !important;
  font-size: 2.25rem !important;
  font-weight: 700 !important;
}

.streaming-content h2 {
  font-size: 1.5rem !important;
  font-weight: 600 !important;
}

.streaming-content h3 {
  font-size: 1.25rem !important;
  font-weight: 600 !important;
}

.streaming-content h4,
.streaming-content h5 {
  font-size: 1.125rem !important;
  font-weight: 600 !important;
}

.streaming-content h6 {
  font-size: 1rem !important;
  font-weight: 600 !important;
}

.streaming-content p {
  margin-bottom: 0.75rem !important;
  line-height: 1.45 !important;
}

/* Fix for MDXEditor popup container */
.mdxeditor-popup-container {
  z-index: 10000 !important;
}

/* Plate.js editor prose styling */
.plate-editor-wrapper [data-slate-editor] {
  min-height: 200px;
  /* Match the previous Toast UI density. `prose-sm` defaults to ~1.71. */
  line-height: 1.45;
}

/*
 * Plate indent-lists wrap each bullet in its own <ul>/<ol>. Tailwind Typography
 * then adds per-list and per-item vertical margins, which stacks into large
 * gaps between consecutive bullets. Keep inter-item spacing to a hairline.
 * Do not zero every paragraph — narrative blocks keep their own gap.
 */
.plate-editor-wrapper [data-slate-editor] :is(ul, ol) {
  margin-top: 0;
  margin-bottom: 0;
}

.plate-editor-wrapper [data-slate-editor] li {
  margin-top: 0;
  margin-bottom: 0.15rem;
  padding-top: 0;
  padding-bottom: 0;
}

.plate-editor-wrapper [data-slate-editor] :is(h1, h2, h3, h4, h5, h6) {
  margin-top: 0.75rem;
  margin-bottom: 0.25rem;
}

.plate-editor-wrapper [data-slate-editor] h1 {
  margin-top: 0;
}

.plate-editor-wrapper-auto-height [data-slate-editor] {
  min-height: 0;
}

.scrollbar-hide {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.scrollbar-hide::-webkit-scrollbar {
  display: none;
}

/* Compact template preview (AdvancedTemplateSelector) */
.template-preview-viewer .plate-editor-wrapper [data-slate-editor] {
  min-height: 0;
}

.template-preview-viewer .prose {
  font-size: 0.875rem;
  line-height: 1.45;
}

.template-preview-viewer .prose h1,
.template-preview-viewer .prose h2,
.template-preview-viewer .prose h3 {
  margin-top: 0.75em;
  margin-bottom: 0.35em;
}

.template-preview-viewer .prose p {
  margin-top: 0.35em;
  margin-bottom: 0.35em;
}

.plate-editor-wrapper-auto-height {
  height: auto !important;
  overflow: visible !important;
}

.scrollbar-hide {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.scrollbar-hide::-webkit-scrollbar {
  display: none;
}

.template-preview-viewer {
  font-size: 0.875rem;
}

.template-preview-viewer p {
  margin: 0.5rem 0;
  line-height: 1.5;
}

.template-preview-viewer h1,
.template-preview-viewer h2,
.template-preview-viewer h3,
.template-preview-viewer h4 {
  margin-top: 1rem;
  margin-bottom: 0.5rem;
  line-height: 1.3;
}

/* Hide React Flow background dots for a clean white canvas */
.react-flow__background {
  display: none !important;
}

/* Genogram node entrance animation — uses standalone `scale` and `opacity`
   so it doesn't override React Flow's `transform: translate()` positioning. */
@keyframes genogram-node-enter {
  0% {
    scale: 0.3;
    opacity: 0;
    filter: drop-shadow(0 0 0 transparent);
  }

  50% {
    scale: 1.08;
    opacity: 1;
    filter: drop-shadow(0 0 10px rgba(65, 96, 209, 0.45));
  }

  70% {
    scale: 0.96;
    filter: drop-shadow(0 0 6px rgba(65, 96, 209, 0.25));
  }

  85% {
    scale: 1.02;
    filter: drop-shadow(0 0 3px rgba(65, 96, 209, 0.1));
  }

  100% {
    scale: 1;
    opacity: 1;
    filter: drop-shadow(0 0 0 transparent);
  }
}

.genogram-node-enter {
  animation: genogram-node-enter 0.55s cubic-bezier(0.34, 1.4, 0.64, 1) both !important;
  z-index: 9999 !important;
}

/* Raise React Flow nodes above siblings on hover so tooltips aren't clipped */
.react-flow__node:hover {
  z-index: 9999 !important;
}

/* Raise edge label renderer above overlays (e.g. legend at z-10) when tooltips are visible */
.react-flow__edgelabel-renderer {
  z-index: 15 !important;
}

.rate-note-section {
  padding: 20px 0;
}

/* Keep native horizontal scrolling but hide the scrollbar chrome - used where we render our
   own left/right scroll-affordance arrows instead (e.g. the template Type filter tabs). */
.scrollbar-hide {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.scrollbar-hide::-webkit-scrollbar {
  display: none;
}

/* Wide markdown tables stay inside the JAN chat bubble. The wrapper's inline
   size ignores the table, so the bubble stays capped. A narrow bubble stacks
   each row; a wider one keeps a normal table that wraps inside the bubble. */
.jan-table {
  max-width: 100%;
  margin-top: 0.5rem;
  margin-bottom: 0.5rem;
  container-type: inline-size;
}

.jan-table table {
  width: 100%;
  margin-top: 0;
  margin-bottom: 0;
  border-collapse: collapse;
}

.jan-table th,
.jan-table td {
  overflow-wrap: break-word;
  vertical-align: top;
}

@container (max-width: 42rem) {
  .jan-table thead {
    position: absolute;
    width: 1px;
    height: 1px;
    margin: -1px;
    padding: 0;
    overflow: hidden;
    clip: rect(0 0 0 0);
    white-space: nowrap;
    border: 0;
  }

  .jan-table table,
  .jan-table tbody,
  .jan-table tr,
  .jan-table td {
    display: block;
    width: auto;
  }

  .jan-table tr + tr {
    margin-top: 0.75rem;
    padding-top: 0.75rem;
    border-top: 1px solid rgb(203 213 225 / 0.9);
  }

  .jan-table td {
    max-width: none;
    padding: 0.45rem 0 0;
    border: 0;
  }

  .jan-table td[data-label]::before {
    content: attr(data-label);
    display: block;
    margin-bottom: 0.1rem;
    font-size: 0.7rem;
    font-weight: 600;
    line-height: 1.2;
    letter-spacing: 0.01em;
    opacity: 0.7;
  }
}
